/**
 * useKanban - Hook para gerenciamento do sistema Kanban de Conversas
 *
 * CHANGELOG:
 *   v2.1.0 (2026-06-26) - Bootstrap migrado para TanStack Query (`useQuery`)
 *     com staleTime de 5 min. Elimina o skeleton "carregando fases" ao
 *     navegar Chat → Kanban: o cache hidrata categorias/fases/inboxes/
 *     agentes/departamentos imediatamente no mount. Mutations (criar/editar/
 *     excluir categoria/fase) atualizam o cache via setQueryData.
 *   v2.0.0 - Refatorado para arquitetura de paginação server-side
 *     Conversas agora são buscadas via useKanbanConversationColumn (por coluna)
 */


import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
// v2.1.0: migrado para sonner — o app só renderiza <Toaster /> do sonner,
// então useToast (legacy) ficava silencioso e usuários não viam mensagens
// de erro/sucesso, em especial as recusas de permissão das RPCs do Kanban.
import { toast } from 'sonner';
import { logger } from '@/lib/logger';


// Tipos do Kanban
export interface KanbanCategory {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  color: string;
  position: number;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface KanbanPhase {
  id: string;
  category_id: string;
  tenant_id: string;
  name: string;
  color: string;
  position: number;
  is_final: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface KanbanConversation {
  id: string;
  kanban_phase_id: string | null;
  contact_id: string | null;
  inbox_id: string;
  status: string;
  unread_count: number;
  last_message_at: string | null;
  last_message_preview: string | null;
  created_at: string;
  assigned_to: string | null;
  department_id: string | null;
  assigned_agent?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  contact?: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
    avatar_url: string | null;
    tags?: Array<{ id: string; name: string; color: string }>;
  } | null;
  /** v1.2.0: Dados do grupo (chat_groups) — preenchidos quando conversa é de grupo */
  group?: {
    id: string;
    name: string | null;
    avatar_url: string | null;
  } | null;
  inbox?: {
    id: string;
    name: string;
    /** v1.3.0: Canal/provider — usado no badge sobre o avatar do card */
    channel_type?: string | null;
    provider_slug?: string | null;
  } | null;
}

export interface KanbanInbox {
  id: string;
  name: string;
  channel_type: string;
}

export interface KanbanAgent {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
}

export interface KanbanDepartment {
  id: string;
  name: string;
}

export function useKanban() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id ?? null;

  // ==========================================================================
  // v2.1.0 - Bootstrap via TanStack Query (com cache global, staleTime 5min)
  // ==========================================================================
  // Antes: useState + useEffect chamavam as 3 RPCs a cada mount → ao voltar
  // para o Kanban, o board exibia o skeleton de "carregando fases" enquanto
  // a RPC `get_kanban_conversation_setup` resolvia. Agora o cache hidrata
  // imediatamente, e o refetch acontece em background sem trocar a UI.
  // ==========================================================================

  type KanbanSetupPayload = {
    departments: KanbanDepartment[];
    categories: KanbanCategory[];
    phases: KanbanPhase[];
  };

  const setupQuery = useQuery<KanbanSetupPayload>({
    queryKey: ['kanban-setup', tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)(
        'get_kanban_conversation_setup',
        { p_tenant_hint: tenantId },
      );
      if (error) throw error;
      const payload = (data || {}) as Partial<KanbanSetupPayload>;
      return {
        departments: payload.departments || [],
        categories: payload.categories || [],
        phases: payload.phases || [],
      };
    },
  });

  const inboxesQuery = useQuery<KanbanInbox[]>({
    queryKey: ['kanban-inboxes', tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_tenant_inboxes_safe', {
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return (data || [])
        .filter((i: any) => i?.status === 'active')
        .map((i: any) => ({
          id: i.id,
          name: i.name,
          channel_type: i.channel_type,
        }));
    },
  });

  const agentsQuery = useQuery<KanbanAgent[]>({
    queryKey: ['kanban-agents', tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)(
        'get_tenant_members_safe',
        { p_tenant_hint: tenantId, p_only_active: true },
      );
      if (error) throw error;
      return (data || []).map((m: any) => ({
        id: m.user_id,
        full_name: m.full_name || null,
        avatar_url: m.avatar_url || null,
      }));
    },
  });

  const categories = setupQuery.data?.categories ?? [];
  const departments = setupQuery.data?.departments ?? [];
  const allPhases = setupQuery.data?.phases ?? [];
  const inboxes = inboxesQuery.data ?? [];
  const agents = agentsQuery.data ?? [];

  // Categoria selecionada (preserva escolha entre sessões via localStorage).
  // Chave: kanban_conversations_selectedCategoryId
  const LS_CATEGORY_KEY = 'kanban_conversations_selectedCategoryId';
  const [selectedCategoryId, _setSelectedCategoryId] = useState<string | null>(() => {
    try { return localStorage.getItem(LS_CATEGORY_KEY); } catch { return null; }
  });
  // Wrapper que persiste em localStorage
  const setSelectedCategoryId = useCallback((id: string | null) => {
    _setSelectedCategoryId(id);
    try {
      if (id) localStorage.setItem(LS_CATEGORY_KEY, id);
      else localStorage.removeItem(LS_CATEGORY_KEY);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (categories.length === 0) {
      _setSelectedCategoryId(null);
      return;
    }
    _setSelectedCategoryId(prev => {
      const next = prev && categories.some(c => c.id === prev) ? prev : categories[0].id;
      // Persiste fallback também para manter consistência
      try { localStorage.setItem(LS_CATEGORY_KEY, next); } catch { /* ignore */ }
      return next;
    });
  }, [categories]);

  // Fases filtradas pela categoria atual (derivado, sem request de rede)
  const phases = useMemo(
    () => (selectedCategoryId
      ? allPhases.filter(p => p.category_id === selectedCategoryId)
      : []),
    [allPhases, selectedCategoryId],
  );

  // Flags de loading compatíveis com a UI atual.
  // `loading` só é true no carregamento inicial real (sem cache).
  const loading =
    (setupQuery.isLoading && !setupQuery.data) ||
    (inboxesQuery.isLoading && !inboxesQuery.data) ||
    (agentsQuery.isLoading && !agentsQuery.data);
  const loadingPhases = setupQuery.isLoading && !setupQuery.data;

  // Estado para filtro por inbox
  const [inboxFilter, setInboxFilterState] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('kanban_inboxFilter') || 'all';
    }
    return 'all';
  });

  // Estado para filtro por agente
  const [agentFilter, setAgentFilterState] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('kanban_agentFilter') || 'all';
    }
    return 'all';
  });

  // Estado para filtro por status de leitura
  const [readStatusFilter, setReadStatusFilterState] = useState<'all' | 'read' | 'unread'>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('kanban_readStatusFilter') as 'all' | 'read' | 'unread') || 'all';
    }
    return 'all';
  });

  // Estado para filtro por departamento
  const [departmentFilter, setDepartmentFilterState] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('kanban_departmentFilter') || 'all';
    }
    return 'all';
  });

  // Termo de busca
  const [searchTerm, setSearchTermState] = useState<string>('');

  // Setters com persistência
  const setInboxFilter = useCallback((value: string) => {
    setInboxFilterState(value);
    if (typeof window !== 'undefined') localStorage.setItem('kanban_inboxFilter', value);
  }, []);

  const setAgentFilter = useCallback((value: string) => {
    setAgentFilterState(value);
    if (typeof window !== 'undefined') localStorage.setItem('kanban_agentFilter', value);
  }, []);

  const setReadStatusFilter = useCallback((value: 'all' | 'read' | 'unread') => {
    setReadStatusFilterState(value);
    if (typeof window !== 'undefined') localStorage.setItem('kanban_readStatusFilter', value);
  }, []);

  const setDepartmentFilter = useCallback((value: string) => {
    setDepartmentFilterState(value);
    if (typeof window !== 'undefined') localStorage.setItem('kanban_departmentFilter', value);
  }, []);

  const setSearchTerm = useCallback((value: string) => {
    setSearchTermState(value);
  }, []);

  // ==========================================================================
  // Helpers para mutar o cache do setup sem refetch (categorias e fases)
  // ==========================================================================
  const patchSetup = useCallback(
    (mutator: (prev: KanbanSetupPayload) => KanbanSetupPayload) => {
      queryClient.setQueryData<KanbanSetupPayload>(
        ['kanban-setup', tenantId],
        prev => mutator(prev ?? { departments: [], categories: [], phases: [] }),
      );
    },
    [queryClient, tenantId],
  );



  /**
   * Helper interno: extrai mensagem amigável de erro de RPC.
   * Detecta erros de permissão (insufficient_privilege) e exibe texto claro.
   */
  const extractErrorMessage = (error: any, fallback: string): string => {
    const code = error?.code || '';
    const msg = error?.message || '';
    // Erros de RLS direto ou de permissão da RPC
    if (code === '42501' || code === '42P17' || /permiss[aã]o|privilege/i.test(msg)) {
      return msg || 'Você não tem permissão para realizar esta ação.';
    }
    if (code === '22023' || /inválid|invalid/i.test(msg)) {
      return msg || fallback;
    }
    return fallback;
  };

  // Cria nova categoria (via RPC com validação de permissão por perfil)
  const createCategory = async (name: string, color: string = '#3b82f6') => {
    if (!currentTenant?.id) return null;
    try {
      const { data, error } = await supabase.rpc('create_kanban_category', {
        p_name: name,
        p_color: color,
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;
      // RPC retorna SETOF -> array; pega o primeiro
      const created = Array.isArray(data) ? data[0] : data;
      if (!created) throw new Error('Categoria não retornada pela RPC');
      patchSetup(prev => ({ ...prev, categories: [...prev.categories, created as KanbanCategory] }));
      setSelectedCategoryId(created.id);
      toast.success('Categoria criada', { description: `"${name}" foi criada.` });
      return created as KanbanCategory;
    } catch (error: any) {
      logger.error('[useKanban] Erro ao criar categoria:', error);
      toast.error(extractErrorMessage(error, 'Não foi possível criar a categoria.'));
      return null;
    }
  };

  // Atualiza categoria (via RPC)
  const updateCategory = async (id: string, updates: Partial<KanbanCategory>) => {
    if (!currentTenant?.id) return null;
    try {
      const { data, error } = await supabase.rpc('update_kanban_category', {
        p_category_id: id,
        p_name: updates.name ?? null,
        p_color: updates.color ?? null,
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      const updated = Array.isArray(data) ? data[0] : data;
      if (!updated) throw new Error('Categoria não retornada pela RPC');
      patchSetup(prev => ({
        ...prev,
        categories: prev.categories.map(cat => cat.id === id ? (updated as KanbanCategory) : cat),
      }));

      return updated as KanbanCategory;
    } catch (error: any) {
      logger.error('[useKanban] Erro ao atualizar categoria:', error);
      toast.error(extractErrorMessage(error, 'Não foi possível atualizar a categoria.'));
      return null;
    }
  };

  // Exclui categoria (via RPC)
  const deleteCategory = async (id: string) => {
    if (!currentTenant?.id) return false;
    try {
      const { error } = await supabase.rpc('delete_kanban_category', {
        p_category_id: id,
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      patchSetup(prev => ({ ...prev, categories: prev.categories.filter(cat => cat.id !== id) }));
      if (selectedCategoryId === id) {
        const remaining = categories.filter(cat => cat.id !== id);
        setSelectedCategoryId(remaining.length > 0 ? remaining[0].id : null);
      }
      toast.success('Categoria excluída', { description: 'A categoria foi excluída.' });
      return true;
    } catch (error: any) {
      logger.error('[useKanban] Erro ao excluir categoria:', error);
      toast.error(extractErrorMessage(error, 'Não foi possível excluir a categoria.'));
      return false;
    }
  };

  /**
   * Reordena as categorias (abas) do Kanban de Conversas — v2.2.0
   *
   * Recebe a lista de IDs já na ordem desejada, atualiza o cache de forma
   * otimista e persiste via RPC segura `reorder_kanban_categories`.
   * Em caso de erro, reverte o cache e propaga a exceção para o componente.
   */
  const reorderCategories = async (categoryIds: string[]) => {
    if (!currentTenant?.id) return false;

    // Snapshot para rollback
    const previous = categories;

    // Atualização otimista: aplica a nova ordem imediatamente no cache
    patchSetup(prev => {
      const byId = new Map(prev.categories.map(cat => [cat.id, cat]));
      const reordenadas = categoryIds
        .map((id, index) => {
          const cat = byId.get(id);
          return cat ? { ...cat, position: index } : null;
        })
        .filter(Boolean) as KanbanCategory[];
      // Mantém eventuais categorias não informadas ao final (defensivo)
      const restantes = prev.categories.filter(cat => !categoryIds.includes(cat.id));
      return { ...prev, categories: [...reordenadas, ...restantes] };
    });

    try {
      const { error } = await supabase.rpc('reorder_kanban_categories', {
        p_category_ids: categoryIds,
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      return true;
    } catch (error: any) {
      logger.error('[useKanban] Erro ao reordenar categorias:', error);
      // Rollback do cache
      patchSetup(prev => ({ ...prev, categories: previous }));
      toast.error(extractErrorMessage(error, 'Não foi possível reordenar as categorias.'));
      throw error;
    }
  };


  // Cria nova fase (via RPC com validação de permissão por perfil)
  const createPhase = async (categoryId: string, name: string, color: string = '#6b7280') => {
    if (!currentTenant?.id) return null;
    try {
      const { data, error } = await supabase.rpc('create_kanban_phase', {
        p_category_id: categoryId,
        p_name: name,
        p_color: color,
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;
      const created = Array.isArray(data) ? data[0] : data;
      if (!created) throw new Error('Fase não retornada pela RPC');
      patchSetup(prev => ({ ...prev, phases: [...prev.phases, created as KanbanPhase] }));

      toast.success('Fase criada', { description: `"${name}" foi criada.` });
      return created as KanbanPhase;
    } catch (error: any) {
      logger.error('[useKanban] Erro ao criar fase:', error);
      toast.error(extractErrorMessage(error, 'Não foi possível criar a fase.'));
      return null;
    }
  };

  // Atualiza fase (via RPC)
  const updatePhase = async (id: string, updates: Partial<KanbanPhase>) => {
    if (!currentTenant?.id) return null;
    try {
      const { data, error } = await supabase.rpc('update_kanban_phase', {
        p_phase_id: id,
        p_name: updates.name ?? null,
        p_color: updates.color ?? null,
        p_is_default: (updates as any).is_default ?? null,
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      const updated = Array.isArray(data) ? data[0] : data;
      if (!updated) throw new Error('Fase não retornada pela RPC');
      patchSetup(prev => ({
        ...prev,
        phases: prev.phases.map(p => p.id === id ? (updated as KanbanPhase) : p),
      }));

      return updated as KanbanPhase;
    } catch (error: any) {
      logger.error('[useKanban] Erro ao atualizar fase:', error);
      toast.error(extractErrorMessage(error, 'Não foi possível atualizar a fase.'));
      return null;
    }
  };

  // Exclui fase (via RPC — desvincula conversas e remove em transação atômica)
  const deletePhase = async (id: string) => {
    if (!currentTenant?.id) return false;
    try {
      const { error } = await supabase.rpc('delete_kanban_phase', {
        p_phase_id: id,
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      patchSetup(prev => ({ ...prev, phases: prev.phases.filter(p => p.id !== id) }));

      toast.success('Fase excluída', { description: 'A fase foi excluída.' });
      return true;
    } catch (error: any) {
      logger.error('[useKanban] Erro ao excluir fase:', error);
      toast.error(extractErrorMessage(error, 'Não foi possível excluir a fase.'));
      return false;
    }
  };

  // Move conversa para outra fase
  const moveConversation = async (conversationId: string, newPhaseId: string) => {
    try {
      const { error } = await supabase
        .from('conversations')
        .update({ kanban_phase_id: newPhaseId })
        .eq('id', conversationId);

      if (error) throw error;

      // Dispara verificação de gatilhos de forma assíncrona
      supabase.functions.invoke('check-manual-triggers', {
        body: { event_type: 'phase_changed', conversation_id: conversationId, new_phase_id: newPhaseId },
      }).catch(err => logger.warn('[useKanban] Erro ao verificar gatilhos:', err));
      
      return true;
    } catch (error) {
      logger.error('[useKanban] Erro ao mover conversa:', error);
      toast.error('Não foi possível mover a conversa.');
      return false;
    }
  };

  // v2.1.0: bootstrap, inboxes e agentes agora são useQuery (cache 5min).
  // Os useEffects de carregamento manual e seleção derivada de fases foram
  // substituídos por derivações via useMemo/useEffect acima.


  return {
    categories,
    phases,
    selectedCategoryId,
    loading,
    loadingPhases,
    inboxes,
    inboxFilter,
    setInboxFilter,
    agents,
    agentFilter,
    setAgentFilter,
    readStatusFilter,
    setReadStatusFilter,
    departments,
    departmentFilter,
    setDepartmentFilter,
    searchTerm,
    setSearchTerm,
    setSelectedCategoryId,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories, // v2.2.0: reordenação das abas por drag and drop

    createPhase,
    updatePhase,
    deletePhase,
    moveConversation,
    // refresh: invalida o cache do setup para forçar refetch em background
    refresh: async () => {
      await queryClient.invalidateQueries({ queryKey: ['kanban-setup', tenantId] });
      await queryClient.invalidateQueries({ queryKey: ['kanban-inboxes', tenantId] });
      await queryClient.invalidateQueries({ queryKey: ['kanban-agents', tenantId] });
    },

  };
}
